import AppIconButton from "../form/AppIconButton";
import { useCallback, useEffect, useMemo, useState } from "react";
import GridLayout, { type LayoutItem, verticalCompactor } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { Alert, Box, Chip, Stack, Tooltip, Typography, alpha } from "@mui/material";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import LinkIcon from "@mui/icons-material/Link";
import { useTranslation } from "react-i18next";
import WidgetOutputView from "../WidgetOutputView";
import DashboardDecorationView from "../DashboardDecorationView";
import { WIDGET_DRAG_MIME } from "../WidgetPalette";
import {
  DECORATION_DRAG_MIME,
  applyGridLayoutChange,
  catalogEntryFor,
  decorationGridKey,
  mergeGridLayout,
  visualGridKey,
  widgetGridKey,
} from "../../utils/decorationCatalog";
import { VISUAL_DRAG_MIME } from "../../utils/visualDesignerDrag";
import type {
  DashboardDecorationKind,
  DashboardDecorationPlacement,
  DashboardExecuteResponse,
  DashboardLayoutZone,
  DashboardVisualPlacement,
  DashboardWidgetPlacement,
  DataQueryExecutionResult,
} from "../../types/dashboard";
import { decorationHasBindings } from "../../utils/resolveDecorationBindings";
import type { WidgetExecuteResponse } from "../../types/widget";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";

const MOBILE_BREAKPOINT = 768;

type Props = {
  placements: DashboardWidgetPlacement[];
  decorations?: DashboardDecorationPlacement[];
  visuals?: DashboardVisualPlacement[];
  results?: DashboardExecuteResponse;
  editable?: boolean;
  rowHeight?: number;
  /** Dashboard layout column count (default 12). Mobile still collapses to 1 column. */
  gridCols?: number;
  zones?: DashboardLayoutZone[];
  loadingWidgetIds?: Set<string>;
  globalLoading?: boolean;
  onLayoutChange?: (placements: DashboardWidgetPlacement[]) => void;
  onGridLayoutChange?: (payload: {
    widgets: DashboardWidgetPlacement[];
    decorations: DashboardDecorationPlacement[];
    visuals: DashboardVisualPlacement[];
  }) => void;
  onWidgetDrop?: (widgetId: string, position?: { x: number; y: number; w: number; h: number }) => void;
  onDecorationDrop?: (
    kind: string,
    position?: { x: number; y: number; w: number; h: number }
  ) => void;
  onVisualDrop?: (
    vizType: string,
    position?: { x: number; y: number; w: number; h: number }
  ) => void;
  onRemoveWidget?: (widgetId: string) => void;
  onRemoveDecoration?: (decorationId: string) => void;
  onEditWidget?: (widgetId: string, currentTitle?: string) => void;
  onEditDecoration?: (decoration: DashboardDecorationPlacement) => void;
  widgetNameById?: Map<string, string>;
  onChartClick?: (widgetId: string, categoryValue: string) => void;
  onVisualChartClick?: (visualId: string, categoryValue: string) => void;
  onVisualSelect?: (visualId: string) => void;
  onVisualEdit?: (visualId: string) => void;
  onRemoveVisual?: (visualId: string) => void;
  selectedVisualId?: string;
  dataQueries?: DataQueryExecutionResult[];
  /** Stretch grid rows to fill the parent height (e.g. public share page). */
  fillContainer?: boolean;
  /** Saved mobile layout: stack every visual in one column. */
  mobileStacked?: boolean;
};

function sortPlacementsForMobile(placements: DashboardWidgetPlacement[]): DashboardWidgetPlacement[] {
  return [...placements].sort((a, b) => a.y - b.y || a.x - b.x);
}

function sortDecorationsForMobile(decorations: DashboardDecorationPlacement[]): DashboardDecorationPlacement[] {
  return [...decorations].sort((a, b) => a.y - b.y || a.x - b.x);
}

function zoneOverlapsPlacement(zone: DashboardLayoutZone, p: DashboardWidgetPlacement): boolean {
  const zx2 = zone.x + zone.w;
  const zy2 = zone.y + zone.h;
  const px2 = p.x + p.w;
  const py2 = p.y + p.h;
  return zone.x < px2 && zx2 > p.x && zone.y < py2 && zy2 > p.y;
}

function visibleZones(
  zones: DashboardLayoutZone[] | undefined,
  placements: DashboardWidgetPlacement[],
  editable: boolean
): DashboardLayoutZone[] {
  if (!editable || !zones?.length) return [];
  return zones.filter((z) => !placements.some((p) => zoneOverlapsPlacement(z, p)));
}

function effectiveGridCols(containerWidth: number, layoutGridCols: number, mobileStacked?: boolean): number {
  if (mobileStacked || containerWidth < MOBILE_BREAKPOINT) return 1;
  return Math.max(1, layoutGridCols);
}

function slicerValues(result?: WidgetExecuteResponse): string[] {
  const payload = result?.payload;
  if (!payload || typeof payload !== "object") return [];
  const rows = (payload as { rows?: unknown }).rows;
  if (!Array.isArray(rows)) return [];
  return rows.flatMap((row) => {
    if (Array.isArray(row)) return row[0] == null ? [] : [String(row[0])];
    if (row && typeof row === "object") {
      const first = Object.values(row as Record<string, unknown>)[0];
      return first == null ? [] : [String(first)];
    }
    return [];
  });
}

function zoneBoxStyle(
  zone: DashboardLayoutZone,
  cols: number,
  layoutRowHeight: number,
  margin: number
) {
  const colPct = 100 / cols;
  return {
    left: `calc(${zone.x * colPct}% + ${margin}px)`,
    width: `calc(${zone.w * colPct}% - ${margin * 2}px)`,
    top: zone.y * (layoutRowHeight + margin),
    height: zone.h * layoutRowHeight + (zone.h - 1) * margin,
  };
}

function isPaletteDrag(types: readonly string[]): boolean {
  return (
    types.includes(WIDGET_DRAG_MIME) ||
    types.includes(DECORATION_DRAG_MIME) ||
    types.includes(VISUAL_DRAG_MIME)
  );
}

function visualDropSize(vizType: string): { w: number; h: number } {
  if (vizType === "card" || vizType === "slicer") return { w: 3, h: vizType === "card" ? 2 : 4 };
  if (vizType === "table") return { w: 6, h: 4 };
  return { w: 6, h: 4 };
}

export default function DashboardGrid({
  placements,
  decorations = [],
  visuals = [],
  results,
  editable = false,
  rowHeight = 80,
  gridCols: layoutGridCols = 12,
  zones,
  loadingWidgetIds,
  globalLoading = false,
  onLayoutChange,
  onGridLayoutChange,
  onWidgetDrop,
  onDecorationDrop,
  onVisualDrop,
  onRemoveWidget,
  onRemoveDecoration,
  onEditWidget,
  onEditDecoration,
  widgetNameById,
  onChartClick,
  onVisualChartClick,
  onVisualSelect,
  onVisualEdit,
  onRemoveVisual,
  selectedVisualId,
  dataQueries,
  fillContainer = false,
  mobileStacked = false,
}: Props) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const styles = custom.components.dashboardGrid;
  const [containerSize, setContainerSize] = useState({ width: 1200, height: 640 });
  const [dropActive, setDropActive] = useState(false);

  useEffect(() => {
    const el = document.getElementById("dashboard-grid-root");
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect) {
        setContainerSize({ width: rect.width, height: rect.height });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [placements.length, decorations.length, visuals.length, fillContainer]);

  const cols = effectiveGridCols(containerSize.width, layoutGridCols, mobileStacked);
  const isMobile = cols === 1;
  const effectiveRowHeight = isMobile ? Math.max(rowHeight, 88) : rowHeight;
  const orderedPlacements = useMemo(
    () => (isMobile ? sortPlacementsForMobile(placements) : placements),
    [isMobile, placements]
  );
  const orderedDecorations = useMemo(
    () => (isMobile ? sortDecorationsForMobile(decorations) : decorations),
    [isMobile, decorations]
  );
  const hintZones = useMemo(
    () => visibleZones(zones, placements, editable),
    [zones, placements, editable]
  );
  const margin = isMobile ? 10 : 12;
  const isEmpty = placements.length === 0 && decorations.length === 0 && visuals.length === 0;
  const gridExtent = useMemo(() => {
    const items = [...orderedPlacements, ...orderedDecorations, ...visuals];
    if (!items.length) return 0;
    return Math.max(...items.map((item) => item.y + item.h));
  }, [orderedPlacements, orderedDecorations, visuals]);

  const layoutRowHeight = useMemo(() => {
    if (!fillContainer || gridExtent <= 0 || containerSize.height <= 0) {
      return effectiveRowHeight;
    }
    const paddingY = isMobile ? 8 : 12;
    const available = containerSize.height - paddingY * 2;
    const totalMargin = margin * (gridExtent + 1);
    const computed = Math.floor((available - totalMargin) / gridExtent);
    if (!Number.isFinite(computed) || computed <= 0) {
      return effectiveRowHeight;
    }
    return Math.max(52, Math.min(computed, 140));
  }, [fillContainer, gridExtent, containerSize.height, margin, effectiveRowHeight, isMobile]);

  const resultMap = useMemo(() => {
    const map = new Map<string, WidgetExecuteResponse>();
    results?.widgets?.forEach((w) => map.set(w.widgetId, w.result));
    return map;
  }, [results]);

  const layout = useMemo(
    () => mergeGridLayout(orderedPlacements, orderedDecorations, cols, editable, visuals),
    [orderedPlacements, orderedDecorations, cols, editable, visuals]
  );

  const handleLayoutChange = useCallback(
    (next: readonly LayoutItem[]) => {
      if (!editable || cols === 1) return;
      const updated = applyGridLayoutChange(orderedPlacements, orderedDecorations, next, visuals);
      if (onGridLayoutChange) {
        onGridLayoutChange(updated);
      } else if (onLayoutChange) {
        onLayoutChange(updated.widgets);
      }
    },
    [editable, onGridLayoutChange, onLayoutChange, orderedPlacements, orderedDecorations, visuals, cols]
  );

  const zoneOverlayHeight = useMemo(() => {
    if (!hintZones.length) return 0;
    const maxY = Math.max(...hintZones.map((z) => z.y + z.h));
    return maxY * (layoutRowHeight + margin) + margin;
  }, [hintZones, layoutRowHeight, margin]);

  const titleFor = (widgetId: string, override?: string) => {
    const fromExec = results?.widgets?.find((w) => w.widgetId === widgetId)?.title;
    return override || fromExec || widgetNameById?.get(widgetId) || widgetId.slice(0, 8);
  };

  const handleExternalDrop = useCallback(
    (layout: readonly LayoutItem[], item: LayoutItem | undefined, e: Event) => {
      setDropActive(false);
      const dragEvent = e as DragEvent;
      const position = item ? { x: item.x, y: item.y, w: item.w, h: item.h } : undefined;
      const widgetId = dragEvent.dataTransfer?.getData(WIDGET_DRAG_MIME);
      if (widgetId && onWidgetDrop) {
        onWidgetDrop(widgetId, position);
        void layout;
        return;
      }
      const kind = dragEvent.dataTransfer?.getData(DECORATION_DRAG_MIME);
      if (kind && onDecorationDrop) {
        onDecorationDrop(kind, position);
        void layout;
        return;
      }
      const vizType = dragEvent.dataTransfer?.getData(VISUAL_DRAG_MIME);
      if (vizType && onVisualDrop) {
        onVisualDrop(vizType, position);
      }
      void layout;
    },
    [onWidgetDrop, onDecorationDrop, onVisualDrop]
  );

  const dropSizeForMime = (types: readonly string[], e?: { dataTransfer?: DataTransfer }) => {
    if (types.includes(WIDGET_DRAG_MIME)) return { w: 6, h: 4 };
    if (types.includes(DECORATION_DRAG_MIME)) {
      const kind = e?.dataTransfer?.getData(DECORATION_DRAG_MIME);
      if (kind) {
        const def = catalogEntryFor(kind as DashboardDecorationKind);
        return { w: Math.min(def.defaultW, cols), h: def.defaultH };
      }
      return { w: 6, h: 3 };
    }
    if (types.includes(VISUAL_DRAG_MIME)) {
      const vizType = e?.dataTransfer?.getData(VISUAL_DRAG_MIME) || "column";
      return visualDropSize(vizType);
    }
    return false;
  };

  return (
    <Box
      id="dashboard-grid-root"
      sx={styles.root(fillContainer)}
      onDragEnter={(e) => {
        if (!editable) return;
        if (isPaletteDrag(e.dataTransfer.types)) setDropActive(true);
      }}
      onDragLeave={(e) => {
        if (!editable) return;
        if (e.currentTarget === e.target) setDropActive(false);
      }}
    >
      {placements.length > 20 && (
        <Alert severity="warning" sx={styles.widgetWarningAlert}>
          {t("dashboard.widgetWarning")}
        </Alert>
      )}

      {isEmpty && (
        <Box
          sx={styles.emptyDropZone(dropActive, hintZones.length > 0 ? zoneOverlayHeight + 48 : undefined)}
          onDragOver={(e) => {
            if (!editable) return;
            if (isPaletteDrag(e.dataTransfer.types)) {
              e.preventDefault();
              setDropActive(true);
            }
          }}
          onDrop={(e) => {
            if (!editable) return;
            e.preventDefault();
            const widgetId = e.dataTransfer.getData(WIDGET_DRAG_MIME);
            if (widgetId && onWidgetDrop) {
              onWidgetDrop(widgetId);
            } else {
              const kind = e.dataTransfer.getData(DECORATION_DRAG_MIME);
              if (kind && onDecorationDrop) {
                onDecorationDrop(kind);
              } else {
                const vizType = e.dataTransfer.getData(VISUAL_DRAG_MIME);
                if (vizType && onVisualDrop) onVisualDrop(vizType);
              }
            }
            setDropActive(false);
          }}
        >
          <DashboardOutlinedIcon sx={styles.emptyDropIcon} />
          <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
            {t("dashboard.dropEmpty")}
          </Typography>
          {editable && hintZones.length > 0 && (
            <Typography variant="caption" color="text.disabled" sx={styles.emptyDropCaption}>
              {t("dashboard.preset.zoneHint")}
            </Typography>
          )}
          {editable && (
            <Typography variant="caption" color="text.disabled" sx={styles.emptyDropCaption}>
              {t("dashboard.decor.dropHint")}
            </Typography>
          )}
          {editable && hintZones.length > 0 && cols > 1 && (
            <Box
              sx={styles.zoneOverlayContainer(zoneOverlayHeight)}
            >
              {hintZones.map((z, idx) => {
                const rect = zoneBoxStyle(z, cols, layoutRowHeight, margin);
                return (
                  <Box
                    key={`${z.labelKey}-${idx}`}
                    sx={styles.zoneBox(rect)}
                  >
                    <Typography variant="caption" color="primary" fontWeight={600}>
                      {z.labelKey ? t(z.labelKey) : ""}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {!isEmpty && (
        <Box sx={styles.gridLayoutWrapper}>
        <GridLayout
          className="layout"
          width={Math.max(0, containerSize.width)}
          layout={layout}
          gridConfig={{ cols, rowHeight: layoutRowHeight, margin: [margin, margin] }}
          dragConfig={{ enabled: editable && cols > 1 }}
          resizeConfig={{ enabled: editable && cols > 1 }}
          compactor={verticalCompactor}
          dropConfig={{ enabled: editable }}
          droppingItem={{ i: "__dropping__", w: 6, h: 4, x: 0, y: 0 }}
          onLayoutChange={handleLayoutChange}
          onDropDragOver={(e) => dropSizeForMime(e.dataTransfer.types, e)}
          onDrop={handleExternalDrop}
          style={styles.gridLayoutStyle(dropActive)}
        >
          {orderedPlacements.map((p) => {
            const loading = globalLoading || loadingWidgetIds?.has(p.widgetId);
            const result = resultMap.get(p.widgetId);
            return (
              <Box
                key={widgetGridKey(p.widgetId)}
                className="dashboard-grid-cell"
                sx={styles.widgetCell}
              >
                {editable && (
                  <Stack
                    direction="row"
                    spacing={0.25}
                    className="no-print"
                    sx={styles.editToolbar}
                  >
                    <Tooltip title={t("dashboard.editWidget")}>
                      <AppIconButton
                        size="small"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => onEditWidget?.(p.widgetId, p.titleOverride || titleFor(p.widgetId))}
                      >
                        <EditIcon fontSize="small" />
                      </AppIconButton>
                    </Tooltip>
                    <Tooltip title={t("dashboard.removeWidget")}>
                      <AppIconButton
                        size="small"
                        color="error"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => onRemoveWidget?.(p.widgetId)}
                      >
                        <CloseIcon fontSize="small" />
                      </AppIconButton>
                    </Tooltip>
                  </Stack>
                )}
                <Box sx={styles.widgetContent}>
                  <WidgetOutputView
                    title={titleFor(p.widgetId, p.titleOverride)}
                    result={result}
                    loading={loading}
                    onChartClick={
                      onChartClick ? (value) => onChartClick(p.widgetId, value) : undefined
                    }
                  />
                </Box>
              </Box>
            );
          })}

          {orderedDecorations.map((d) => (
            <Box
              key={decorationGridKey(String(d.id))}
              className="dashboard-grid-cell"
              sx={styles.decorationCell}
            >
              {decorationHasBindings(d) && (
                <Tooltip title={t("dashboard.decor.bindingIcon")}>
                  <AppIconButton size="small" sx={styles.bindingIcon} tabIndex={-1}>
                    <LinkIcon fontSize="small" color="primary" />
                  </AppIconButton>
                </Tooltip>
              )}
              {editable && (
                <Stack
                  direction="row"
                  spacing={0.25}
                  className="no-print"
                  sx={styles.editToolbar}
                >
                  <Tooltip title={t("dashboard.decor.edit")}>
                    <AppIconButton
                      size="small"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={() => onEditDecoration?.(d)}
                    >
                      <EditIcon fontSize="small" />
                    </AppIconButton>
                  </Tooltip>
                  <Tooltip title={t("dashboard.decor.remove")}>
                    <AppIconButton
                      size="small"
                      color="error"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={() => onRemoveDecoration?.(d.id)}
                    >
                      <CloseIcon fontSize="small" />
                    </AppIconButton>
                  </Tooltip>
                </Stack>
              )}
              <DashboardDecorationView item={d} dataQueries={dataQueries} />
            </Box>
          ))}

          {visuals.map((visual) => {
            const result = results?.visuals?.find((item) => item.visualId === visual.id)?.result;
            const selected = selectedVisualId === visual.id;
            return (
              <Box
                key={visualGridKey(String(visual.id))}
                className="dashboard-grid-cell"
                onMouseDown={() => visual.id && onVisualSelect?.(visual.id)}
                sx={{
                  ...styles.widgetCell,
                  outline: selected ? "2px solid #1a73e8" : undefined,
                  borderRadius: 1,
                  zIndex: visual.z ?? 1,
                }}
              >
                {editable && (
                  <Stack direction="row" spacing={0.25} className="no-print" sx={styles.editToolbar}>
                    <Tooltip title={t("reportDesigner.editVisual")}>
                      <AppIconButton
                        size="small"
                        color="primary"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!visual.id) return;
                          (onVisualEdit || onVisualSelect)?.(visual.id);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </AppIconButton>
                    </Tooltip>
                    <Tooltip title={t("dashboard.removeWidget")}>
                      <AppIconButton
                        size="small"
                        color="error"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => visual.id && onRemoveVisual?.(visual.id)}
                      >
                        <CloseIcon fontSize="small" />
                      </AppIconButton>
                    </Tooltip>
                  </Stack>
                )}
                <Box sx={styles.widgetContent}>
                  {visual.vizType === "slicer" ? (
                    <Box sx={{ p: 1, overflow: "auto", height: "100%" }}>
                      <Typography variant="subtitle2">{visual.title || visual.vizType}</Typography>
                      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5} sx={{ mt: 1 }}>
                        {slicerValues(result).map((value) => (
                          <Chip
                            key={value}
                            size="small"
                            label={value}
                            onClick={() => visual.id && onVisualChartClick?.(visual.id, value)}
                          />
                        ))}
                      </Stack>
                    </Box>
                  ) : !Object.values(visual.wells || {}).some((fields) => fields?.length) && !result ? (
                    <Box sx={{ p: 1.5 }}>
                      <Typography variant="subtitle2">{visual.title || visual.vizType}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("reportDesigner.dropField")}
                      </Typography>
                    </Box>
                  ) : (
                    <WidgetOutputView
                      title={visual.title || visual.vizType || ""}
                      result={result}
                      loading={globalLoading}
                      onChartClick={
                        onVisualChartClick && visual.id
                          ? (value) => onVisualChartClick(visual.id as string, value)
                          : undefined
                      }
                    />
                  )}
                </Box>
              </Box>
            );
          })}
        </GridLayout>
        </Box>
      )}
    </Box>
  );
}
